import React, { useState, useEffect, useRef, useMemo } from 'react';
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { BarLoader } from 'react-spinners';
import { ThemeContext } from '../../components/darkmode/themeContext';

import { Fonts } from '../../components/theme/fonts';
import { Containers } from '../../components/theme/containers';
import { Styling } from '../../components/theme/styling';
import { Images } from '../../assets/img';
import { Spacing } from '../../components/theme/spacing';
import { btnTxt, btnTxtCatTag, btnTxtSize, btnTxtSizeMin, Buttons } from '../../components/theme/buttons';

import { IoArrowBackOutline, IoChevronDown, IoClose, IoSearch } from "react-icons/io5";
import { BsFillCaretDownFill } from 'react-icons/bs';
import { GrClearOption, GrTag } from "react-icons/gr";
import { AiOutlineClear, AiOutlineTags, AiTwotoneTag } from "react-icons/ai";
import { FaCheck } from "react-icons/fa";
import { IoIosCloseCircle } from "react-icons/io";
import { RiSearch2Line } from 'react-icons/ri';

// const API_BASE = "https://resolyou.org/blog/wp-json/wp/v2";
const API_BASE = "/wp-blog/wp-json/wp/v2";

export const Blog = () => {
    const navigate = useNavigate();
    const { slug } = useParams();
    const { theme } = React.useContext(ThemeContext);
    const [posts, setPosts] = useState([]);
    const [activePost, setActivePost] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingBlog, setLoadingBlog] = useState(true);
    const [loadingPost, setLoadingPost] = useState(false);
    const [loadingPostId, setLoadingPostId] = useState(null);
    const [lastScrollPostId, setLastScrollPostId] = useState(null);
    const [scrollBackPostId, setScrollBackPostId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    const [categoryOpen, setCategoryOpen] = useState(false);
    const [tagOpen, setTagOpen] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [imageLoading, setImageLoading] = useState(true);

    // const [activeTag, setActiveTag] = useState(null);

    const dropdownRef = useRef(null);

    const listRef = useRef(null); // container of posts
    const postRefs = useRef({});  // individual post list items
    const activePostRef = useRef(null);

    useEffect(() => {
        setLoading(true)
        /* setTimeout(() => {
            setLoading(false)
        }, 1300) */
    }, []);

    useEffect(() => {
        if (!slug) {
            setActivePost(null);
            return;
        }

        setLoading(true);

        fetch(`${API_BASE}/posts?slug=${slug}&_embed`)
            .then(res => res.json())
            .then(data => {
                setActivePost(data[0] || null);
            })
            .finally(() => setLoading(false));
    }, [slug]);


    useEffect(() => {
        fetch(`${API_BASE}/posts?per_page=10&_embed`)
            .then(res => res.json())
            .then(data => {
                setPosts(data);
                setLoadingBlog(false);
                setLoading(false)
            })
            .catch(err => {
                console.error(err);
                setLoadingBlog(false);
                setLoading(false)
            });
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setCategoryOpen(false);
                setTagOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    const loadPost = (id) => {
        setLoadingPostId(id);
        setImageLoading(true);

        // Scroll to top when opening the post
        if (activePostRef.current) {
            activePostRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }

        fetch(`${API_BASE}/posts/${id}?_embed`)
            .then(res => res.json())
            .then(data => {
                setActivePost(data);
                setLoadingPostId(null);
            })
            .catch(err => {
                console.error(err);
                setLoadingPostId(null);
            });
    };

    const formatDate = (wpDate) => {
        const d = new Date(wpDate);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

    useEffect(() => {
        if (!activePost && scrollBackPostId) {
            const timeout = setTimeout(() => {
                const el = postRefs.current[scrollBackPostId];
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                setScrollBackPostId(null);
            }, 150); // slightly longer to allow animations to finish

            return () => clearTimeout(timeout);
        } else if (activePost) {
            const el = activePostRef.current;
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [activePost, scrollBackPostId]);

    const [hasSearched, setHasSearched] = useState(false);

    const handleSearch = async () => {
        if (!searchQuery.trim()) return;

        setSearching(true);
        setHasSearched(true);

        try {
            const res = await fetch(
                `${API_BASE}/posts?search=${encodeURIComponent(searchQuery)}&_embed`
            );
            const data = await res.json();
            setSearchResults(data);
        } catch (err) {
            console.error('Error searching posts:', err);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };


    const [activeCategory, setActiveCategory] = useState(null);

    const allCategories = useMemo(() => {
        const map = new Map();

        posts.forEach(post => {
            post._embedded?.['wp:term']?.[0]?.forEach(category => {
                map.set(category.id, category);
            });
        });

        return Array.from(map.values());
    }, [posts]);

    const allTags = useMemo(() => {
        const map = new Map();

        posts.forEach(post => {
            post._embedded?.['wp:term']?.[1]?.forEach(tag => {
                map.set(tag.id, tag);
            });
        });

        return Array.from(map.values());
    }, [posts]);

    const toggleTag = (tagId) => {
        setSelectedTags(prev =>
            prev.includes(tagId)
                ? prev.filter(id => id !== tagId)
                : [...prev, tagId]
        );
    };

    const clearTags = () => setSelectedTags([]);

    // const isSearchActive = searchQuery.trim().length > 0;

    // const basePosts = isSearchActive ? searchResults : posts;
    const basePosts = hasSearched ? searchResults : posts;

    const visiblePosts = basePosts.filter(post => {
        const categories = post._embedded?.['wp:term']?.[0] || [];
        const tags = post._embedded?.['wp:term']?.[1] || [];

        const matchesCategory =
            !activeCategory ||
            categories.some(cat => cat.id === activeCategory);

        const matchesTag =
            selectedTags.length === 0 ||
            tags.some(tag => selectedTags.includes(tag.id));

        return matchesCategory && matchesTag;
    });

    const loadingRef = useRef(null)

    const handleReadArticle = (post) => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });

        navigate(`/blog/${post.slug}`);
    };


    /*  useEffect(() => {
         setSelectedTags([]);
     }, [activeCategory]);
  */

    /*  useEffect(() => {
         setActiveCategory(null);
     }, [selectedTags]); */

    /* useEffect(() => {
        if ((loading || searching) && loadingRef.current) {
            loadingRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }
    }, [handleReadArticle]); */


    return (
        <>
            {loading || searching ?
                <div
                    className={`${Styling.flexCenter} relative z-20`}
                >
                    <div
                        className={`
                        ${Containers.glsFx} 
                        ${Containers.mainContainer}
                        `}
                    // ref={loadingRef}
                    >
                        <motion.div
                            className={`${Styling.flexCenter} ${Containers.carouselSubContainer}`}
                            transition={{
                                duration: 1.2,
                                ease: "easeInOut"
                            }}
                            exit={{
                                opacity: 0
                            }}
                            animate={{
                                opacity: 1,
                                y: 0
                            }}
                            initial={{
                                opacity: 0,
                                y: 0
                            }}
                        >
                            {theme === 'dark' ? (
                                <BarLoader
                                    color={'hsl(207, 100%, 65%)'}
                                    loading={true}
                                    size={50}
                                    speedMultiplier='0.9'
                                />
                            ) : (
                                <BarLoader
                                    color={'hsl(211, 100%, 18%)'}
                                    loading={true}
                                    size={50}
                                    speedMultiplier='0.9'
                                />
                            )}
                        </motion.div>
                    </div>
                </div>
                :
                <AnimatePresence mode='wait'>
                    <motion.div
                        transition={{
                            duration: 0.75,
                            ease: "easeInOut"
                        }}
                        exit={{
                            opacity: 0
                        }}
                        animate={{
                            opacity: 1,
                            y: 0
                        }}
                        initial={{
                            opacity: 0,
                            y: 0
                        }}
                    >
                        <div
                            className={`${Styling.flexCenter} relative z-0`}
                        >
                            <div
                                className={`
                                        ${[Containers.glsFx, Containers.mainCarouselContainer, Containers.mainContainer]}
                                    `}>
                                {!activePost && (
                                    <motion.div
                                        key="post-list"
                                        className="flex flex-col w-full 2xsm:min-h-[400px] lg:min-h-[350px] xl:min-h-[400px] 2xl:min-h-[500px]"
                                        transition={{ duration: 0.75, ease: "easeInOut" }}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                    >
                                        <div
                                            className="flex xsm:flex-col lg:flex-row justify-center items-center w-full 2xsm:py-[1.25rem] lg:py-[1rem] bg-slate-200/[50%] dark:bg-black/20 rounded-t-lg shadow-inner dark:shadow-none 2xsm:gap-y-[1rem] lg:gap-y-0 border-t border-x border-slate-700/20 dark:border-secondary/10">
                                            <div className="relative xsm:w-full lg:w-1/2 2xsm:px-[1rem] xsm:px-[1.5rem] lg:px-[2rem] lg:border-r lg:border-slate-500/40 lg:dark:border-secondaryDark/50">
                                                <div
                                                    className='relative w-full'
                                                >
                                                    <input
                                                        type="text"
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        placeholder="Search blog posts..."
                                                        className={`w-full xsm:h-[2.5rem] 2xl:h-[3rem] 2xsm:pl-[1rem] lg:pl-[1.25rem] 2xl:pl-[1.5rem] pr-[5.5rem] border rounded-full placeholder-slate-500 dark:placeholder-white/90 focus:outline-none focus:ring-2 focus:ring-secondary bg-white border-gray-400 shadow-md dark:border-secondary/20 dark:bg-darkerPrimary text-slate-800 dark:text-white !font-[600] ${Fonts.bodyFont}`}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleSearch();
                                                        }}
                                                    />

                                                    {/* Right-side icons */}
                                                    <div className="absolute flex items-center gap-2 -translate-y-1/2 2xsm:right-1 lg:right-2 2xl:right-2 top-1/2">
                                                        {/* Clear */}
                                                        {searchQuery && !searching && (
                                                            <button
                                                                onClick={() => {
                                                                    setSearchQuery('');
                                                                    setSearchResults([]);
                                                                    setHasSearched(false)
                                                                }}
                                                                className={`flex items-center justify-center w-8 h-8 text-[1.5rem] rounded-full text-red-700 hover:text-red-900 dark:text-gray-400 dark:hover:text-white ${Styling.transitionStyleFaster}`}
                                                                aria-label="Clear search"
                                                            >
                                                                <IoClose />
                                                            </button>
                                                        )}

                                                        {/* Search / Loader */}
                                                        <button
                                                            onClick={handleSearch}
                                                            className={`flex items-center justify-center 2xsm:w-8 2xsm:h-8 lg:w-7 lg:h-7 2xl:w-8 2xl:h-8 p-2 rounded-full bg-secondary dark:bg-darkSecondary hover:bg-primary dark:hover:bg-primary text-white ${Styling.transitionStyleFaster}`}
                                                            aria-label="Search"
                                                        >
                                                            {searching ? (
                                                                <BarLoader
                                                                    color="white"
                                                                    loading={true}
                                                                    width={14}
                                                                    height={2}
                                                                />
                                                            ) : (
                                                                <RiSearch2Line />
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                className='relative flex flex-row items-center justify-center xsm:w-full lg:w-1/2 2xsm:gap-x-[0.5rem] lg:gap-x-[0.75rem] 2xl:gap-x-[1rem]'
                                            >
                                                <div
                                                    ref={dropdownRef}
                                                    className="w-1/2 2xsm:pl-[1rem] xsm:pl-[1.5rem] lg:pl-[2rem]"
                                                >
                                                    <div
                                                        className='relative'
                                                    >
                                                        <button
                                                            onClick={() => {
                                                                setCategoryOpen((prev) => !prev)
                                                                setTagOpen(false);
                                                            }}
                                                            className={`mnuFont flex items-center justify-between w-full 2xsm:px-[0.75rem] lg:px-[1rem] 2xl:px-[1.5rem] xsm:h-[2.5rem] 2xl:h-[3rem] rounded-full bg-white dark:bg-darkerPrimary dark:hover:bg-secondary hover:bg-secondary  hover:text-white dark:text-white transition !font-[600] border border-gray-400 shadow-md dark:border-secondary/20 dark:hover:border-secondary dark:shadow-none ${btnTxtCatTag} ${activeCategory ? 'text-slate-800' : 'text-slate-500'}`}
                                                        >
                                                            <span className='2xsm:pl-[0.5rem] lg:pl-0'>
                                                                {activeCategory
                                                                    ? allCategories.find((c) => c.id === activeCategory)?.name
                                                                    : 'Filter Category'}
                                                            </span>

                                                            <BsFillCaretDownFill
                                                                className={`ml-2 ${Styling.transitionStyleMedium} ${categoryOpen ? 'rotate-[540deg]' : 'rotate-0'
                                                                    }`}
                                                            />
                                                        </button>

                                                        {/* Dropdown */}
                                                        <AnimatePresence>
                                                            {categoryOpen && (
                                                                <motion.ul
                                                                    initial={{ opacity: 0, y: -8 }}
                                                                    animate={{ opacity: 1, y: 0 }}
                                                                    exit={{ opacity: 0, y: -8 }}
                                                                    transition={{ duration: 0.2, ease: 'easeOut' }}
                                                                    className={`mnuFont absolute z-50 w-full mt-[0.75rem] overflow-hidden border border-darkPrimary/50 dark:border-primary/50 shadow-md dark:shadow-none text-white bg-darkPrimary/85 dark:bg-darkerPrimary/80 dark:text-white top-full backdrop-blur-[12px] rounded-[3px] ${btnTxtSize}`}
                                                                >
                                                                    {/* All */}
                                                                    <li>
                                                                        <button
                                                                            onClick={() => {
                                                                                setActiveCategory(null);
                                                                                setCategoryOpen(false);
                                                                                setTagOpen(false);
                                                                            }}
                                                                            className={`w-full text-left 2xsm:px-[1rem] lg:px-[1.25rem] 2xl:px-[1.5rem] 2xsm:py-[1.15rem] lg:py-[1.15rem] hover:bg-secondary hover:text-white transition ${!activeCategory ? 'font-bold text-lime-500' : 'dark:text-white text-white font-semibold'}`}
                                                                        >
                                                                            All
                                                                        </button>
                                                                    </li>

                                                                    {allCategories.map((category) => (
                                                                        <li key={category.id}>
                                                                            <button
                                                                                onClick={() => {
                                                                                    setActiveCategory(category.id);
                                                                                    setCategoryOpen(false);
                                                                                    setTagOpen(false);
                                                                                }}
                                                                                className={`w-full text-left 2xsm:px-[1rem] lg:px-[1.25rem] 2xl:px-[1.5rem] 2xsm:py-[1.15rem] lg:py-[1.15rem] hover:bg-secondaryDark dark:hover:bg-secondaryDark hover:text-white dark:hover:text-white transition ${activeCategory === category.id ? 'font-bold text-lime-500 hover:text-lime-300 focus:text-lime-300' : 'dark:text-white text-white font-semibold'}`}
                                                                            >
                                                                                {category.name}
                                                                            </button>
                                                                        </li>
                                                                    ))}
                                                                </motion.ul>
                                                            )}
                                                        </AnimatePresence>
                                                    </div>
                                                </div>
                                                <div className={`relative w-1/2 max-w-[50%] min-w-0 ${(activeCategory !== null || selectedTags.length > 0) ? '' : '2xsm:pr-[1rem] xsm:pr-[1.5rem] lg:pr-[2rem]'}`}>
                                                    <button
                                                        onClick={() => {
                                                            setTagOpen(prev => !prev)
                                                            setCategoryOpen(false)
                                                        }}
                                                        className={`mnuFont truncate flex items-center justify-between w-full 2xsm:px-[0.75rem] lg:px-[1rem] 2xl:px-[1.5rem] 2xsm:h-[2.5rem] 2xl:h-[3rem] rounded-full bg-white dark:bg-darkerPrimary dark:hover:bg-secondary hover:bg-secondary text-slate-800 hover:text-white dark:text-white transition !font-[600] border border-gray-400 shadow-md dark:border-secondary/20 dark:hover:border-secondary dark:shadow-none ${btnTxtCatTag} ${selectedTags.length === 0 ? 'text-slate-500' : 'text-slate-800'}`}
                                                    >
                                                        <span className={`truncate 2xsm:pl-[0.5rem] lg:pl-0 ${selectedTags.length > 0 ? 'lowercase' : ''}`}>
                                                            {selectedTags.length === 0
                                                                ? 'Filter Tag'
                                                                : selectedTags
                                                                    .map(id => allTags.find(t => t.id === id)?.name)
                                                                    .join(', ')
                                                            }
                                                        </span>

                                                        <BsFillCaretDownFill
                                                            className={`ml-2 ${Styling.transitionStyleMedium} ${tagOpen ? 'rotate-[540deg]' : 'rotate-0'
                                                                }`}
                                                        />
                                                    </button>

                                                    <AnimatePresence>
                                                        {tagOpen && (
                                                            <motion.ul
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                                initial={{ opacity: 0, y: -8 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                exit={{ opacity: 0, y: -8 }}
                                                                transition={{ duration: 0.2, ease: 'easeOut' }}
                                                                className={`mnuFont absolute z-50 mt-[0.75rem] overflow-hidden border border-darkPrimary/50 dark:border-primary/50 shadow-md dark:shadow-none bg-darkPrimary/85 dark:bg-darkerPrimary/80 text-white top-full backdrop-blur-[12px] rounded-[3px] ${btnTxtSize} ${(activeCategory !== null || selectedTags.length > 0) ? 'w-[calc(100%+0.5rem)]' : '2xsm:w-[calc(100%-0.5rem)] lg:w-[calc(100%-2rem)]'}`}
                                                            >
                                                                {/* All */}
                                                                <li>
                                                                    <button
                                                                        onClick={clearTags}
                                                                        className={`w-full text-left 2xsm:px-[1rem] lg:px-[1.25rem] 2xl:px-[1.5rem] 2xsm:py-[1.15rem] lg:py-[1.15rem] hover:bg-secondary transition ${selectedTags.length === 0 ? 'font-bold text-lime-500' : 'font-semibold'}`}
                                                                    >
                                                                        All
                                                                    </button>
                                                                </li>


                                                                {allTags.map(tag => {
                                                                    const isSelected = selectedTags.includes(tag.id);

                                                                    return (
                                                                        <li key={tag.id}>
                                                                            <button
                                                                                onClick={() => toggleTag(tag.id)}
                                                                                className={`w-full flex items-center justify-between 2xsm:px-[1rem] lg:px-[1.25rem] 2xl:px-[1.5rem] 2xsm:py-[1.15rem] lg:py-[1.15rem] hover:bg-secondaryDark lowercase transition ${isSelected ? 'font-bold text-lime-500 hover:text-lime-300 focus:text-lime-300' : 'font-semibold'}`}
                                                                            >
                                                                                # {tag.name}
                                                                                {isSelected && <FaCheck />}
                                                                            </button>
                                                                        </li>
                                                                    );
                                                                })}
                                                                <li>
                                                                    <button
                                                                        onClick={clearTags}
                                                                        className={`mnuFont relative flex flex-row items-center justify-center w-full text-left 2xl:px-[1.5rem] 2xsm:py-[1rem] lg:py-[1rem] 2xl:py-[1.15rem] bg-red-800 hover:bg-red-900 text-white transition ${Styling.transitionStyleMedium} ${selectedTags.length === 0 ? 'pointer-events-none opacity-70' : 'font-semibold'}`}
                                                                    >
                                                                        <AiOutlineClear className='absolute left-[1rem] !text-white 2xsm:text-[0.9rem] lg:text-[0.95rem] 2xl:text-[1rem]' />
                                                                        Clear Tags
                                                                    </button>
                                                                </li>
                                                            </motion.ul>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                                {(activeCategory !== null || selectedTags.length > 0) && (
                                                    <button
                                                        onClick={() => {
                                                            setActiveCategory(null);
                                                            clearTags();
                                                        }}
                                                        className={`relative flex flex-row items-center justify-center text-red-800 hover:text-red-700 ${Styling.transitionStyleFast} xsm:mr-[1.5rem] lg:mx-[1rem]`}
                                                    >
                                                        <IoIosCloseCircle className='text-[1.5rem]' />
                                                    </button>
                                                )}
                                            </div>

                                        </div>
                                        <div className={`border-t-slate-700/20 border-x-white/20 border-b-white/20 dark:border-secondary/10 ${Containers.blogContainer} border-x border-t border-b 2xsm:min-h-[400px] lg:min-h-[350px] xl:min-h-[400px] 2xl:min-h-[500px] `}>
                                            <div className={`${Styling.flexY} h-auto flex-col 2xsm:w-full 2xsm:px-0 2xl:px-[2rem]`}>
                                                {loadingBlog ? (
                                                    <BarLoader
                                                        color={theme === 'dark' ? 'hsl(207, 100%, 65%)' : 'hsl(211, 100%, 18%)'}
                                                        loading={loading}
                                                        size={50}
                                                        speedMultiplier="0.9"
                                                    />
                                                ) : (
                                                    <>
                                                        {visiblePosts.length === 0 &&
                                                            !searching && (hasSearched || activeCategory !== null || selectedTags.length > 0) ? (
                                                            <motion.p
                                                                key="no-posts"
                                                                className={`py-10 text-center ${Fonts.noPostsFont}`}
                                                                initial={{ opacity: 0 }}
                                                                animate={{ opacity: 1 }}
                                                                exit={{ opacity: 0 }}
                                                                transition={{
                                                                    duration: 0.5,
                                                                    delay: 0.25,
                                                                    ease: 'easeOut',
                                                                }}
                                                            >
                                                                No posts found.
                                                            </motion.p>
                                                        ) : (
                                                            <motion.ul
                                                                // ref={listRef}
                                                                // layout
                                                                className="relative grid 2xsm:grid-cols-1 lg:grid-cols-2 xsm:gap-y-[1rem] lg:gap-y-[1rem] 2xl:gap-y-[2rem] lg:gap-x-[1.5rem] xl:gap-x-[2rem] 2xl:gap-x-[2rem] lg:auto-rows-fr"
                                                            >
                                                                {/*  <div
                                                                    className="absolute top-0 bottom-0 w-px -translate-x-1/2 pointer-events-none left-1/2 bg-secondary/20 2xsm:hidden lg:block"
                                                                /> */}
                                                                <AnimatePresence mode="wait">
                                                                    {visiblePosts.map((post, index) => (
                                                                        <motion.li
                                                                            key={post.id}
                                                                            ref={el => postRefs.current[post.id] = el}
                                                                            layout
                                                                            initial={{ opacity: 0, scale: 0.98 }}
                                                                            animate={{ opacity: 1, scale: 1 }}
                                                                            exit={{ opacity: 0, scale: 0.95 }}
                                                                            transition={{
                                                                                layout: { duration: 0.45, ease: "easeInOut" },
                                                                                opacity: { duration: 0.25 }
                                                                            }}
                                                                            className="flex flex-col h-full bg-slate-50/50 dark:bg-darkestPrimary/20 border border-secondary/30 dark:border-secondary/20 2xsm:px-[1.5rem] 2xsm:pb-[1rem] lg:px-[1.75rem] lg:pb-[1.25rem] 2xl:px-[2rem] 2xl:pb-[1.5rem] rounded-[6px]"
                                                                        >
                                                                            {/* ${visiblePosts.length > 1 && index < visiblePosts.length - 1
                                                                                    ? 'border-b-[5px] border-dotted border-gray-200 dark:border-secondary/20 pb-[1.5rem]'
                                                                                    : 'border-0 pb-[1rem]'
                                                                                } */}

                                                                            <div className="flex flex-row items-center justify-between 2xsm:py-[1rem] lg:py-[1.25rem] 2xl:py-[1.5rem] mb-[0rem]">
                                                                                {/* border-y-[1px] border-secondary/30 */}
                                                                                <h2 className={`${Fonts.blogTitleFont} flex justify-start`}>
                                                                                    {post.title.rendered}
                                                                                </h2>
                                                                                <div
                                                                                    className='flex flex-row items-center gap-x-2'
                                                                                >
                                                                                    <p className={`${Fonts.dateLabel} text-slate-700/80 dark:text-lightSecondary/80`}>
                                                                                        Posted:
                                                                                    </p>
                                                                                    <p className={`${Fonts.dateFont} !font-[800]`}>
                                                                                        {formatDate(post.date)}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                            <div
                                                                                className='flex items-center justify-center 2xsm:max-h-[10rem] lg:max-h-[10rem] xl:max-h-[12rem] 2xl:max-h-[16rem] mb-4 rounded-[3px] overflow-hidden border border-darkPrimary/80 dark:border-secondary/20'
                                                                            >
                                                                                <img
                                                                                    src={post._embedded?.['wp:featuredmedia']?.[0]?.source_url || 'https://via.placeholder.com/600x400'}
                                                                                    alt={post.title.rendered}
                                                                                    className="object-contain w-full"
                                                                                    onLoad={() => setImageLoading(false)}
                                                                                    onError={() => setImageLoading(false)}
                                                                                />
                                                                            </div>

                                                                            <div
                                                                                className='flex flex-row items-center justify-start w-full 2xsm:border-b-[3px] lg:border-b-[3.5px] 2xl:border-b-[5px] border-dotted border-secondary/20 pb-[1rem]'
                                                                            >
                                                                                <div className="flex flex-row items-center gap-2 mr-[1rem] xsm:w-[30%] lg:w-[50%]">
                                                                                    <p className={`${Fonts.categoryFont} text-slate-700/80 dark:text-lightSecondary/80`}>
                                                                                        Category
                                                                                    </p>
                                                                                    {post._embedded?.['wp:term']?.[0]?.map(category => (
                                                                                        <button
                                                                                            key={category.id}
                                                                                            onClick={() => {
                                                                                                setActiveCategory(category.id);
                                                                                            }}
                                                                                            className={`${Fonts.categoryFont}
                                                                                              xsm:px-[0.75rem] 2xl:px-[1rem] xsm:py-[0.25rem] 2xl:py-[0.5rem] rounded-full
                                                                                              bg-gray-500/20 dark:bg-darkerPrimary
                                                                                              text-primary hover:text-white dark:text-secondary dark:hover:text-white
                                                                                              border border-gray-700 dark:border-secondary/80
                                                                                              hover:bg-secondary dark:hover:bg-secondary
                                                                                              transition cursor-pointer !font-[800]
                                                                                            `}
                                                                                        >
                                                                                            {category.name}
                                                                                        </button>
                                                                                    ))}
                                                                                </div>
                                                                                {post._embedded?.['wp:term']?.[1]?.length > 0 && (
                                                                                    <div className="flex-row flex items-center justify-end xsm:w-[70%] lg:w-[50%] flex-wrap">
                                                                                        <AiOutlineTags className={`text-[1.25rem] text-slate-700/80 dark:text-lightSecondary/80 2xsm:mr-[0.5rem] 2xl:mr-[1rem]`} />
                                                                                        {post._embedded?.['wp:term']?.[1]?.map(tag => (
                                                                                            <button
                                                                                                key={tag.id}
                                                                                                onClick={() => {
                                                                                                    setSelectedTags(prev =>
                                                                                                        prev.includes(tag.id)
                                                                                                            ? prev.filter(id => id !== tag.id)
                                                                                                            : [...prev, tag.id]
                                                                                                    );
                                                                                                }}

                                                                                                className={`${Fonts.tagFont}
                                                                                                  px-[0.25rem] py-[0.15rem]
                                                                                                  text-gray-500 hover:text-gray-800 dark:text-secondary dark:hover:text-white
                                                                                                  transition cursor-pointer !font-[800] hover:decoration-black hover:underline underline-offset-4 lowercase ${Styling.transitionStyleFaster}`}
                                                                                            >
                                                                                                #{tag.name}
                                                                                            </button>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                            <div className="flex flex-col w-full mt-[1rem] lg:flex-1 2xsm:min-h-[200px] lg:min-h-[100px]">
                                                                                <div
                                                                                    className={`${Fonts.blogPrevFont} flex-1 xsm:mb-[1.5rem] lg:mb-[1rem]`}
                                                                                    dangerouslySetInnerHTML={{
                                                                                        __html: post.excerpt.rendered
                                                                                    }}
                                                                                />
                                                                                <div
                                                                                    className='flex items-center justify-end mt-auto border-t border-secondary/20 pt-[1rem]'
                                                                                >
                                                                                    <button
                                                                                        // onClick={() => loadPost(post.id)}
                                                                                        onClick={() => handleReadArticle(post)}
                                                                                        className={`flex items-center justify-center  2xsm:!w-[9rem] lg:!w-[10rem] 2xl:!w-[12rem] self-end ${Buttons.btnAction} ${Styling.transitionStyleFaster}`}
                                                                                    >
                                                                                        {loadingPostId === post.id ? (
                                                                                            <BarLoader
                                                                                                color={'hsl(207, 100%, 85%)'}
                                                                                                loading={true}
                                                                                                size={2}
                                                                                                width={60}
                                                                                                speedMultiplier="0.9"
                                                                                            />
                                                                                        ) : (
                                                                                            'Read Article'
                                                                                        )}
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </motion.li>
                                                                    ))}
                                                                </AnimatePresence>
                                                            </motion.ul>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                    </motion.div>
                                )}
                                {activePost && (
                                    <AnimatePresence mode='wait'>
                                        <motion.div
                                            key={loadingPost}
                                            ref={activePostRef}
                                            transition={{
                                                duration: 0.75,
                                                ease: "easeInOut"
                                            }}
                                            exit={{
                                                opacity: 0
                                            }}
                                            animate={{
                                                opacity: 1,
                                                y: 0
                                            }}
                                            initial={{
                                                opacity: 0,
                                                y: 0
                                            }}
                                            className='w-full'
                                        >
                                            <div

                                                className='2xsm:min-h-[400px] lg:min-h-[350px] xl:min-h-[400px] 2xl:min-h-[500px] w-full 2xsm:py-[1.5rem] lg:py-[1rem] 2xl:py-[2rem] 2xsm:px-[2rem] lg:px-[3rem] 2xl:px-[5rem]'
                                            >

                                                <div
                                                    className='relative flex justify-between w-full h-full 2xsm:items-center lg:items-center pb-[1.5rem]'
                                                >
                                                    <button
                                                        onClick={() => {
                                                            if (activePost?.id) {
                                                                setScrollBackPostId(activePost.id); // pass the current active post id directly
                                                            }
                                                            // setActivePost(null);
                                                            navigate(-1);
                                                        }}
                                                        className={`absolute 2xsm:-left-[1.5rem] lg:-left-[2rem] 2xl:-left-[2.5rem] 2xsm:text-[1.15rem] lg:text-[1.25rem] 2xl:text-[1.5rem] text-secondary dark:text-lightSecondary hover:text-primary dark:hover:text-white ${Styling.transitionStyleFaster}`}
                                                    >
                                                        <IoArrowBackOutline />
                                                    </button>
                                                    <h2 className={`${Fonts.blogTitleFont} flex justify-start`}>
                                                        {activePost.title.rendered}
                                                    </h2>
                                                    <div
                                                        className='flex flex-row items-center 2xsm:gap-x-1 lg:gap-2 '
                                                    >
                                                        <p className={`${Fonts.dateLabel} ${btnTxtSizeMin} text-slate-700/80 dark:text-lightSecondary/80`}>
                                                            Posted:
                                                        </p>
                                                        <p className={`${Fonts.dateFont} h-full !font-[800] text-slate-700 dark:text-white`}>
                                                            {formatDate(activePost.date)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div
                                                    className='relative flex items-center justify-center w-full border border-darkPrimary/40 dark:border-primary/40 rounded-[6px] overflow-hidden'
                                                >
                                                    <img
                                                        src={activePost._embedded?.['wp:featuredmedia']?.[0]?.source_url || 'https://via.placeholder.com/600x400'}
                                                        alt={activePost.title.rendered}
                                                        className="object-cover w-full 2xsm:max-h-[10rem] lg:max-h-[16rem] xl:max-h-[20rem] 2xl:max-h-[27rem] "
                                                    />
                                                </div>

                                                <div
                                                    className='flex flex-row items-center justify-between gap-x-[2rem] 2xsm:py-[1rem] lg:py-[1.25rem] 2xl:py-[1.5rem] mb-[1rem] w-full 2xsm:border-b-[3px] lg:border-b-[3.5px] 2xl:border-b-[5px] border-dotted border-secondary/15'
                                                >
                                                    <div
                                                        className='flex flex-row items-center justify-between w-full'
                                                    >
                                                        <div
                                                            className='flex flex-row items-center justify-start w-1/2 2xsm:gap-x-1 lg:gap-x-2'
                                                        >
                                                            <p className={`${Fonts.bodyFont} ${btnTxtSizeMin} text-slate-700/80 dark:text-lightSecondary/80`}>
                                                                Category:
                                                            </p>
                                                            {activePost._embedded?.['wp:term']?.[0]?.map(category => (
                                                                <p
                                                                    key={category.id}
                                                                    className={`${Fonts.bodyFont} ${btnTxtSizeMin} !font-[800]`}
                                                                >
                                                                    {category.name}
                                                                </p>
                                                            ))}
                                                        </div>
                                                        <div
                                                            className='flex flex-row flex-wrap items-center justify-end gap-x-[0.75rem] w-1/2'
                                                        >
                                                            <AiOutlineTags className={`2xsm:text-[1rem] lg:text-[1.25rem] text-slate-700/80 dark:text-secondary mr-[0.25rem]`} />
                                                            {activePost._embedded?.['wp:term']?.[1]?.map(tag => (
                                                                <p
                                                                    key={tag.id}
                                                                    className={`${Fonts.tagFont} py-[0.15rem] text-gray-500 dark:text-secondary !font-[800] lowercase`}
                                                                >
                                                                    #{tag.name}
                                                                </p>
                                                            ))}
                                                        </div>
                                                    </div>

                                                </div>
                                                <div
                                                >
                                                    <div
                                                        className={`prose prose-lg sm:prose-xl ${Fonts.bodyFont} max-w-full min-h-[100px]`}
                                                        dangerouslySetInnerHTML={{
                                                            __html: activePost.content.rendered
                                                        }}
                                                    />
                                                    <div
                                                        className='border-t border-secondary/40 xsm:mt-[1.5rem] lg:mt-[2rem]'
                                                    >
                                                        <button
                                                            onClick={() => {
                                                                if (activePost?.id) {
                                                                    setScrollBackPostId(activePost.id); // pass the current active post id directly
                                                                }
                                                                // setActivePost(null);
                                                                navigate(-1);
                                                            }}
                                                            className={`2xsm:px-[3rem] 2xl:px-[4rem] xsm:mt-[1.5rem] lg:mt-[2rem] mb-2  ${Buttons.btnAction} ${Styling.transitionStyleFaster}`}
                                                        >
                                                            Go Back
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>
            }
            {/* </motion.div> */}
        </>
    )
}